import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { surveyPhoto, handlePhotoError } from "../../utils/profilePhoto";
import rating1 from "../../assets/img/rating1.png";
import rating2 from "../../assets/img/rating2.png";
import rating3 from "../../assets/img/rating3.png";
import rating4 from "../../assets/img/rating4.png";
import rating5 from "../../assets/img/rating5.png";
import staricon from "../../assets/img/staricon.svg";
import sprinklesBg from "../../assets/img/sprinkles_bg.png";
import sadEmoji from "../../assets/img/sad.png";
import happyEmoji from "../../assets/img/happy.png";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getSurveyForMe, getSurveyForMeDetails, submitSurvey, getPreviewSubmittedSurvey, completeSurvey } from "../../service/surveyForMe";
import { isSurveyExpired } from "../../utils/surveyPresentation";

// Mapping of rating images to labels
const ratingsMap = [
  { id: 1, label: "Very Dissatisfied", img: rating1 },
  { id: 2, label: "Dissatisfied", img: rating2 },
  { id: 3, label: "Neutral", img: rating3 },
  { id: 4, label: "Satisfied", img: rating4 },
  { id: 5, label: "Very Satisfied", img: rating5 }
];

const isOtherOption = (optionText) => /^(other|other \(please specify\))$/i.test(optionText.trim());

const TakeSurvey = () => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const location = useLocation();

  // Retrieve survey metadata passed from the dashboard card
  const [surveyMeta, setSurveyMeta] = useState(location.state?.surveyMeta || {
    title: "Survey",
    description: "",
    rewardCoins: 0,
    endDate: null,
    author: { name: "Survey creator", avatar: "" }
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submittedAnswersDict, setSubmittedAnswersDict] = useState({});
  const [otherSpecify, setOtherSpecify] = useState({});
  const [originalOtherSpecifyDict, setOriginalOtherSpecifyDict] = useState({});
  const [step, setStep] = useState("questions"); // "questions" | "preview" | "success"

  useEffect(() => {
    if (location.state?.surveyMeta) {
      setSurveyMeta(location.state.surveyMeta);
      return;
    }

    let isMounted = true;
    const loadSurveyMeta = async () => {
      for (const tab of ["available", "saved", "completed"]) {
        try {
          const response = await getSurveyForMe(tab);
          const survey = (response.data?.data || []).find(
            (item) => String(item.id) === String(surveyId)
          );
          if (survey) {
            if (isMounted) {
              setSurveyMeta({
                title: survey.survey_title || "Survey",
                description: survey.survey_description || "",
                rewardCoins: Number(survey.reward_per_participant) || 0,
                endDate: survey.end_date,
                author: {
                  name: survey.creator_name || "Survey creator",
                  avatar: survey.profile_photo || survey.author_avatar || "",
                },
              });
            }
            return;
          }
        } catch (error) {
          console.warn(`Could not load survey metadata from ${tab}:`, error);
        }
      }
    };

    loadSurveyMeta();
    return () => { isMounted = false; };
  }, [surveyId, location.state?.surveyMeta]);

  useEffect(() => {
    if (!isSurveyExpired(surveyMeta.endDate)) return;
    toast.error("This survey has expired.");
    navigate("/survey-for-me", { replace: true });
  }, [surveyMeta.endDate, navigate]);

  // Fetch questions on mount
  useEffect(() => {
    let isMounted = true;

    const fetchQuestionsAndAnswers = async () => {
      try {
        setLoading(true);

        // 1. Fetch survey questions
        const questionsResponse = await getSurveyForMeDetails({ survey_id: Number(surveyId) });
        if (!isMounted) return;
        if (!questionsResponse) {
          toast.error("Failed to load survey questions.");
          navigate("/survey-for-me");
          return;
        }

        let fetchedQuestions = [];
        if (questionsResponse.data && questionsResponse.data.success) {
          fetchedQuestions = questionsResponse.data.data || [];
          // Sort questions by question_order
          fetchedQuestions.sort((a, b) => (a.question_order || 0) - (b.question_order || 0));
          setQuestions(fetchedQuestions);
        } else {
          toast.error("Failed to load survey questions.");
          navigate("/survey-for-me");
          return;
        }

        // 2. Fetch previously submitted answers if any
        let submittedAnswers = [];
        try {
          const previewResponse = await getPreviewSubmittedSurvey(Number(surveyId));
          if (isMounted && previewResponse && previewResponse.data && previewResponse.data.success) {
            submittedAnswers = previewResponse.data.data || [];
          }
        } catch (previewErr) {
          console.warn("Could not load preview submitted survey:", previewErr);
        }

        if (!isMounted) return;

        // 3. Initialize and restore answers structure
        const initialAnswers = {};
        const initialOtherSpecify = {};

        fetchedQuestions.forEach(q => {
          const submittedQ = submittedAnswers.find(sa => sa.question_id === q.question_id);

          if (q.question_type === "multi_select") {
            if (submittedQ && submittedQ.selected_options && submittedQ.selected_options.length > 0) {
              initialAnswers[q.question_id] = submittedQ.selected_options.map(opt => opt.option_text);
              if (submittedQ.answer) {
                initialOtherSpecify[q.question_id] = submittedQ.answer;
              }
            } else {
              initialAnswers[q.question_id] = [];
            }
          } else if (q.question_type === "rating") {
            if (submittedQ && submittedQ.selected_options && submittedQ.selected_options.length > 0) {
              const selectedOption = submittedQ.selected_options[0];
              const optionIndex = (q.options || []).findIndex((option) =>
                String(option.option_id ?? option.id) === String(selectedOption.option_id ?? selectedOption.id)
                || option.option_text === selectedOption.option_text
              );
              const ratingId = optionIndex >= 0 ? optionIndex + 1 : Number(selectedOption.option_text);
              const matchedRating = ratingsMap.find(r => r.id === ratingId);
              initialAnswers[q.question_id] = {
                ratingId: ratingId || null,
                label: q.options?.[ratingId - 1]?.option_text || matchedRating?.label || "",
                comment: submittedQ.answer || ""
              };
            } else {
              initialAnswers[q.question_id] = { ratingId: null, label: "", comment: "" };
            }
          } else if (q.question_type === "single_select") {
            if (submittedQ && submittedQ.selected_options && submittedQ.selected_options.length > 0) {
              initialAnswers[q.question_id] = submittedQ.selected_options[0].option_text;
            } else {
              initialAnswers[q.question_id] = "";
            }
          } else {
            // open_end
            if (submittedQ && submittedQ.answer) {
              initialAnswers[q.question_id] = submittedQ.answer;
            } else {
              initialAnswers[q.question_id] = "";
            }
          }
        });

        setAnswers(initialAnswers);
        setOtherSpecify(initialOtherSpecify);
        setSubmittedAnswersDict(JSON.parse(JSON.stringify(initialAnswers)));
        setOriginalOtherSpecifyDict(JSON.parse(JSON.stringify(initialOtherSpecify)));

        // 4. Find the first unanswered question index or default to preview
        let firstUnansweredIndex = 0;
        for (let i = 0; i < fetchedQuestions.length; i++) {
          const q = fetchedQuestions[i];
          const ans = initialAnswers[q.question_id];
          const isAnswered =
            q.question_type === "multi_select"
              ? ans && ans.length > 0
              : q.question_type === "rating"
                ? ans && ans.ratingId !== null
                : ans && (typeof ans === "string" && ans.trim() !== "");

          if (!isAnswered) {
            firstUnansweredIndex = i;
            break;
          }
          if (i === fetchedQuestions.length - 1) {
            firstUnansweredIndex = fetchedQuestions.length;
          }
        }

        if (firstUnansweredIndex >= fetchedQuestions.length && fetchedQuestions.length > 0) {
          setCurrentQuestionIndex(fetchedQuestions.length - 1);
          setStep("preview");
        } else {
          setCurrentQuestionIndex(firstUnansweredIndex);
          setStep("questions");
        }

      } catch (err) {
        if (!isMounted) return;
        console.error("Error fetching survey details:", err);
        toast.error("An error occurred while loading survey questions.");
        navigate("/survey-for-me");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchQuestionsAndAnswers();

    return () => {
      isMounted = false;
    };
  }, [surveyId, navigate]);

  // Scroll to top when active question or step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentQuestionIndex, step]);

  // General select option / answer update handler
  const handleUpdateAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Multi-select option checkbox toggle handler
  const handleToggleMultiSelect = (questionId, optionText) => {
    const currentSelection = Array.isArray(answers[questionId]) ? answers[questionId] : [];
    let nextSelection;
    if (currentSelection.includes(optionText)) {
      nextSelection = currentSelection.filter(item => item !== optionText);
    } else {
      nextSelection = [...currentSelection, optionText];
    }
    handleUpdateAnswer(questionId, nextSelection);
  };

  // Handler for custom inputs in multi-select options (e.g. specify other)
  const handleOtherSpecifyChange = (questionId, text) => {
    setOtherSpecify(prev => ({
      ...prev,
      [questionId]: text
    }));
  };

  // Rating option select handler
  const handleSelectRating = (questionId, ratingId, label) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ratingId,
        label
      }
    }));
  };

  // Rating comment text update handler
  const handleRatingCommentChange = (questionId, commentText) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        comment: commentText
      }
    }));
  };

  // Helper to check if active answer has changed compared to last saved server value
  const isAnswerChanged = (q) => {
    if (!q) return false;
    const current = answers[q.question_id];
    const original = submittedAnswersDict[q.question_id];

    if (q.question_type === "multi_select") {
      const currArr = current || [];
      const origArr = original || [];
      if (currArr.length !== origArr.length) return true;
      const sortedCurr = [...currArr].sort();
      const sortedOrig = [...origArr].sort();
      for (let i = 0; i < sortedCurr.length; i++) {
        if (sortedCurr[i] !== sortedOrig[i]) return true;
      }

      // Check specify text
      if (currArr.some(isOtherOption)) {
        if (otherSpecify[q.question_id] !== originalOtherSpecifyDict[q.question_id]) {
          return true;
        }
      }
      return false;
    }

    if (q.question_type === "rating") {
      if (!current || !original) {
        return current !== original;
      }
      if (current.ratingId !== original.ratingId) return true;
      if (current.comment !== original.comment) return true;
      return false;
    }

    // single_select or open_end
    return current !== original;
  };

  // Navigation handlers
  const handleNext = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    const answer = answers[currentQuestion.question_id];

    // Every survey question must be answered before continuing.
    const isAnswerEmpty =
      currentQuestion.question_type === "multi_select"
        ? !answer || answer.length === 0
        : currentQuestion.question_type === "rating"
          ? !answer || answer.ratingId === null
          : !answer || (typeof answer === "string" && answer.trim() === "");

    if (isAnswerEmpty) {
      toast.warn("This question is required. Please provide an answer.");
      return;
    }

    // Check if the answer has actually changed
    const hasChanged = isAnswerChanged(currentQuestion);

    if (!hasChanged) {
      // Just navigate forward instantly without API call
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        setStep("preview");
      }
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    try {
      let payload = {
        survey_id: Number(surveyId),
        question_id: currentQuestion.question_id
      };

      if (
        currentQuestion.question_type === "single_select" ||
        currentQuestion.question_type === "multi_select" ||
        currentQuestion.question_type === "rating"
      ) {
        let selectedOptionTexts = [];
        if (currentQuestion.question_type === "single_select") {
          selectedOptionTexts = answer ? [answer] : [];
        } else if (currentQuestion.question_type === "multi_select") {
          selectedOptionTexts = answer || [];
        } else if (currentQuestion.question_type === "rating") {
          selectedOptionTexts = answer?.ratingId
            ? [currentQuestion.options?.[answer.ratingId - 1]?.option_text || String(answer.ratingId)]
            : [];
        }

        const selectedOptionIds = [];
        if (currentQuestion.options && currentQuestion.options.length > 0) {
          selectedOptionTexts.forEach(text => {
            const matchedOpt = currentQuestion.options.find(opt => opt.option_text === text);
            const optionId = matchedOpt?.option_id ?? matchedOpt?.id;
            if (optionId != null) {
              selectedOptionIds.push(optionId);
            }
          });
        }

        if (selectedOptionTexts.length && selectedOptionIds.length !== selectedOptionTexts.length) {
          toast.error("Unable to match the selected answer. Please reload the survey and try again.");
          return;
        }

        payload.option_ids = selectedOptionIds;

        // Support optional comments or specify-other responses
        if (currentQuestion.question_type === "rating") {
          if (answer?.comment) {
            payload.answer = answer.comment;
          }
        } else if (currentQuestion.question_type === "multi_select") {
          const otherVal = otherSpecify[currentQuestion.question_id];
          if (otherVal) {
            payload.answer = otherVal;
          }
        }
      } else {
        // open_end
        payload.answer = answer || "";
      }

      // Call submitSurvey API with flat payload structure
      const response = await submitSurvey(payload);
      if (response && response.data && response.data.success) {
        // Update reference states representing saved backend values
        setSubmittedAnswersDict(prev => ({
          ...prev,
          [currentQuestion.question_id]: JSON.parse(JSON.stringify(answer))
        }));
        if (currentQuestion.question_type === "multi_select") {
          setOriginalOtherSpecifyDict(prev => ({
            ...prev,
            [currentQuestion.question_id]: otherSpecify[currentQuestion.question_id]
          }));
        }

        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
        } else {
          setStep("preview");
        }
      } else {
        const msg = response?.data?.message || "Failed to save response. Please try again.";
        toast.error(msg);
      }
    } catch (err) {
      console.error("Error submitting answer:", err);
      const errMsg = err.response?.data?.message || "An error occurred while saving your response.";
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleExit = async () => {
    const result = await Swal.fire({
      title: `<div class="d-flex align-items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC3545" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0;"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>Exit Survey?</div>`,
      text: "Answers submitted with Next are saved. You can resume this survey later.",
      showCancelButton: true,
      confirmButtonText: "Continue Survey →",
      cancelButtonText: "Quit Survey",
      reverseButtons: true,
      customClass: {
        popup: "swal2-exit-popup",
        title: "swal2-exit-title",
        htmlContainer: "swal2-exit-text",
        actions: "swal2-exit-actions",
        confirmButton: "swal-confirm-btn",
        cancelButton: "swal-cancel-btn"
      },
      buttonsStyling: false
    });

    if (result.dismiss === Swal.DismissReason.cancel) {
      navigate("/survey-for-me");
    }
  };

  const handleSubmitSurvey = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const response = await completeSurvey({ survey_id: Number(surveyId) });
      if (response && response.data && response.data.success) {
        setStep("success");
      } else {
        toast.error(response?.data?.message || "Failed to submit survey.");
      }
    } catch (error) {
      console.error("Error submitting survey:", error);
      toast.error(error.response?.data?.message || "An error occurred while submitting the survey.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatReviewAnswer = (q) => {
    const val = answers[q.question_id];
    if (q.question_type === "multi_select") {
      if (!val || val.length === 0) return "No Answer Selected";
      let displayString = val.join(", ");
      if (val.includes("Other (Please Specify)") || val.some(s => s.toLowerCase().includes("other"))) {
        const otherVal = otherSpecify[q.question_id];
        if (otherVal) {
          displayString += ` (${otherVal})`;
        }
      }
      return displayString;
    }
    if (q.question_type === "rating") {
      if (!val || val.ratingId === undefined || val.ratingId === null) return "No Rating Selected";
      let displayString = `${val.label || ""}`;
      if (val.comment) {
        displayString += ` - "${val.comment}"`;
      }
      return displayString;
    }
    return val || "No Response Entered";
  };

  if (loading) {
    return (
      <div className="take-survey-page d-flex flex-column align-items-center justify-content-center py-5 bg-light" style={{ minHeight: "80vh" }}>
        <div className="sfm-loading">
          <div className="spinner"></div>
          <p className="mt-3 text-muted">Loading survey questions...</p>
        </div>
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <div className="take-survey-page p-4 text-center">
        <div className="sfm-empty-state bg-white p-5 border-light rounded-4 shadow-sm mx-auto mt-5" style={{ maxWidth: "600px" }}>
          <h3 className="fw-bold mb-3">No questions found</h3>
          <p className="text-muted mb-4">We couldn't load any questions for this survey. Please try another survey.</p>
          <button className="btn btn-danger bgred hover-bg-dark border-0 px-4 py-2 rounded-3 fw-bold ft-13" onClick={() => navigate("/survey-for-me")}>
            Back to Surveys
          </button>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="take-survey-page p-4 d-flex align-items-center justify-content-center" style={{ minHeight: "85vh" }}>
        {/* Survey Completed Card */}
        <div
          className="sfm-success-container border-light rounded-4 text-center mx-auto shadow-sm d-flex flex-column align-items-center justify-content-center p-5"
          style={{
            maxWidth: "600px",
            width: "100%",
            height: "520px",
            background: `url(${sprinklesBg}) no-repeat center top, linear-gradient(180deg, #EDFFF1 0%, #FFFFFF 100%)`,
            backgroundSize: "529px 304px, cover",
            border: "1px solid #e9ecef"
          }}
        >
          <div className="celebration-confetti mb-4">
            <div className="check-outer-circle mx-auto mb-3">
              <div className="check-inner-circle d-flex align-items-center justify-content-center">
                <i className="bi bi-check-lg text-white ft-36"></i>
              </div>
            </div>
            <h2 className="fw-bold ft-24 mb-1">🎉 Survey Completed!</h2>
            <p className="text-muted ft-14 mb-4">Thank You For Your Participation</p>
          </div>

          <div className="earned-badge-container py-3 px-4 rounded-3 mb-4 mx-auto" style={{ minWidth: "200px", maxWidth: "240px", backgroundColor: "#E8FFED" }}>
            <span className="text-muted ft-12 d-block">You Earned</span>
            <span className="ft-20 fw-bold text-dark d-inline-flex align-items-center gap-2 mt-1">
              <img src={staricon} alt="coins" style={{ width: "22px", height: "22px" }} /> #{surveyMeta.rewardCoins} Coins
            </span>
          </div>

          <p className="text-muted ft-13 mb-5 px-3">
            Your contribution helps the community understand remote collaboration trends.
          </p>

          <div className="d-flex gap-3 justify-content-center w-100">
            <button
              className="btn px-4 py-3 rounded-[4px] fw-bold ft-14"
              style={{ border: "1px solid #858C95", color: "#2c3038", backgroundColor: "#fff" }}
              onClick={() => navigate("/survey-for-me", { replace: true })}
            >
              Back To Surveys
            </button>
            <button className="btn btn-danger px-4 py-3 rounded-3 fw-bold ft-14 bgred hover-bg-dark border-0" onClick={() => navigate("/my-wallet", { replace: true })}>
              View Rewards
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "preview") {
    return (
      <div className="take-survey-page p-4">
        {/* Header Bar */}
        <div className="sfm-header-nav d-flex align-items-center justify-content-between bg-white p-4 border-light rounded-4 mb-4 shadow-sm">
          <div className="d-flex align-items-center gap-3 cursor-pointer" onClick={handleExit} style={{ userSelect: "none", cursor: "pointer" }}>
            <div className="d-flex align-items-center justify-content-center rounded-circle" style={{ width: "36px", height: "36px", backgroundColor: "#fff1f2", transition: "transform 0.2s", cursor: "pointer" }} onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"} onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}>
              <i className="bi bi-chevron-left text-dark fw-bold cursor-pointer" style={{ fontSize: "14px", cursor: "pointer" }}></i>
            </div>
            <span className="fw-bold text-dark ft-15" style={{ cursor: "pointer" }}>Exit</span>
          </div>
          <div className="sfm-reward-top d-flex align-items-center gap-2 bg-light-orange px-4 py-2 rounded-pill ft-13">
            <img src={staricon} alt="star" style={{ width: "16px", height: "16px" }} />
            <span className="text-dark fw-medium">Reward: <span className="fw-bold">#{surveyMeta.rewardCoins} Coins</span></span>
          </div>
        </div>

        {/* Info Header Card */}
        <div className="sfm-survey-info-card bg-white p-4 border-light rounded-4 mb-4 shadow-sm">
          <div className="d-flex align-items-center gap-3 mb-3">
            <img src={surveyPhoto(surveyMeta)} onError={handlePhotoError} className="sfm-avatar" alt="Author" style={{ width: "42px", height: "42px" }} />
            <div>
              <h4 className="m-0 ft-14 fw-bold">{surveyMeta.author?.name || "Aisha Mehta"}</h4>
              <small className="text-muted ft-11">Product Researcher - <span className="text-danger">Survey Creator</span></small>
            </div>
          </div>
          <h2 className="ft-18 fw-bold mb-2 text-dark">{surveyMeta.title}</h2>
          <p className="text-muted ft-12 m-0 line-height-18">{surveyMeta.description}</p>
        </div>

        {/* Review Answers container */}
        <div className="sfm-question-container bg-white p-4 border-light rounded-4 shadow-sm mb-4">
          <h3 className="fw-bold ft-16 text-dark mb-1">Review Your Answers</h3>
          <p className="text-muted ft-12 mb-4">You're all set! Please review your answers before submitting.</p>

          <div className="review-list d-flex flex-column gap-3 mb-4">
            {questions.map((q, idx) => (
              <div
                key={q.question_id}
                className="review-item-row d-flex align-items-center justify-content-between p-3 border rounded-3 cursor-pointer hover-bg-light"
                onClick={() => {
                  setCurrentQuestionIndex(idx);
                  setStep("questions");
                }}
              >
                <div className="review-q-info pe-3">
                  <span className="ft-13 fw-bold text-dark d-block mb-1">
                    Q{idx + 1}. {q.question_text}
                  </span>
                </div>
                <div className="review-a-info d-flex align-items-center gap-2 flex-shrink-0 text-muted ft-12">
                  <span className="review-answer-text text-truncate" style={{ maxWidth: "200px" }}>
                    {formatReviewAnswer(q)}
                  </span>
                  <i className="bi bi-chevron-down text-muted"></i>
                </div>
              </div>
            ))}
          </div>

          <div className="survey-actions-footer border-top pt-4 mt-4 d-flex justify-content-between align-items-center">
            <div className="auto-save-label text-success ft-12 d-flex align-items-center gap-2">
              <i className="bi bi-shield-check ft-16"></i> Your submitted answers are saved
            </div>
            <div className="d-flex gap-3">
              <button className="btn btn-prev px-4 py-2 rounded-3 fw-semibold ft-13" onClick={() => setStep("questions")}>
                <ArrowLeft size={16} /> Previous
              </button>
              <button 
                className="btn btn-danger btn-next-submit px-4 py-2 rounded-3 fw-semibold ft-13 bgred hover-bg-dark border-0" 
                onClick={handleSubmitSurvey}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Survey"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progressPercent = Math.round((currentQuestionIndex / questions.length) * 100);
  const hasChanged = currentQuestion ? isAnswerChanged(currentQuestion) : false;


  return (
    <div className="take-survey-page p-4">
      {/* Header Bar */}
      <div className="sfm-header-nav d-flex align-items-center justify-content-between bg-white p-4 border-light rounded-4 mb-4 shadow-sm">
        <div className="d-flex align-items-center gap-3 cursor-pointer" onClick={handleExit} style={{ userSelect: "none", cursor: "pointer" }}>
          <div className="d-flex align-items-center justify-content-center rounded-circle" style={{ width: "36px", height: "36px", backgroundColor: "#fff1f2", transition: "transform 0.2s", cursor: "pointer" }} onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"} onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}>
            <i className="bi bi-chevron-left text-dark fw-bold cursor-pointer" style={{ fontSize: "14px", cursor: "pointer" }}></i>
          </div>
          <span className="fw-bold text-dark ft-15" style={{ cursor: "pointer" }}>Exit</span>
        </div>
        <div className="sfm-reward-top d-flex align-items-center gap-2 bg-light-orange px-4 py-2 rounded-pill ft-13">
          <img src={staricon} alt="star" style={{ width: "16px", height: "16px" }} />
          <span className="text-dark fw-medium">Reward: <span className="fw-bold">#{surveyMeta.rewardCoins} Coins</span></span>
        </div>
      </div>

      {/* Info Header Card */}
      <div className="sfm-survey-info-card bg-white p-4 border-light rounded-4 mb-4 shadow-sm">
        <div className="d-flex align-items-center gap-3 mb-3">
          <img src={surveyPhoto(surveyMeta)} onError={handlePhotoError} className="sfm-avatar" alt="Author" style={{ width: "42px", height: "42px" }} />
          <div>
            <h4 className="m-0 ft-14 fw-bold">{surveyMeta.author?.name || "Survey creator"}</h4>
            <small className="text-muted ft-11">Product Researcher - <span className="text-danger">Survey Creator</span></small>
          </div>
        </div>
        <h2 className="ft-18 fw-bold mb-2 text-dark">{surveyMeta.title}</h2>
        <p className="text-muted ft-12 m-0 line-height-18">{surveyMeta.description}</p>
      </div>

      {/* active question container */}
      <div className="sfm-question-container bg-white p-4 border-light rounded-4 shadow-sm">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fw-semibold ft-13 text-muted">Question {currentQuestionIndex + 1}/{questions.length}</span>
          <span className="fw-semibold ft-13 text-muted">{progressPercent}%</span>
        </div>

        <div className="cs-progress-track mb-4">
          <div className="cs-progress-fill orange-progress" style={{ width: `${progressPercent}%` }}></div>
        </div>

        <h5 className="ft-12 text-muted mb-3">Question {currentQuestionIndex + 1}</h5>
        <h3 className="ft-16 fw-bold mb-5 text-dark">{currentQuestion.question_text}</h3>

        {/* branch by question_type */}
        <div className="survey-question-options-area min-height-240">
          {currentQuestion.question_type === "single_select" && (
            <div className="d-flex flex-column gap-2 mb-4">
              {(currentQuestion.options || []).map(option => {
                const isSelected = answers[currentQuestion.question_id] === option.option_text;
                return (
                  <label key={option.option_id} className="survey-option-label">
                    <input
                      type="radio"
                      name={`q-${currentQuestion.question_id}`}
                      className="form-check-input mt-0"
                      checked={isSelected}
                      onChange={() => handleUpdateAnswer(currentQuestion.question_id, option.option_text)}
                    />
                    <span className="ft-14 text-dark fw-normal">{option.option_text}</span>
                  </label>
                );
              })}
            </div>
          )}

          {currentQuestion.question_type === "multi_select" && (
            <div className="d-flex flex-column gap-2 mb-4">
              {(currentQuestion.options || []).map(option => {
                const isSelected = Array.isArray(answers[currentQuestion.question_id]) && answers[currentQuestion.question_id].includes(option.option_text);
                return (
                  <div key={option.option_id} className="d-flex flex-column">
                    <label className="survey-option-label">
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={isSelected}
                        onChange={() => handleToggleMultiSelect(currentQuestion.question_id, option.option_text)}
                      />
                      <span className="ft-14 text-dark fw-normal">{option.option_text}</span>
                    </label>
                    {isOtherOption(option.option_text) && isSelected && (
                      <div className="mt-2 ps-4">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Please specify other option..."
                          value={otherSpecify[currentQuestion.question_id] || ""}
                          onChange={(e) => handleOtherSpecifyChange(currentQuestion.question_id, e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {currentQuestion.question_type === "rating" && (
            <div className="d-flex flex-column mb-4">
              <p className="text-muted ft-13 mb-4">Select a rating from 1 to 5.</p>
              
              {/* Emoji scale row */}
              <div className="d-flex justify-content-between align-items-end mb-4" style={{ padding: "0 1px" }}>
                <div className="d-flex flex-column align-items-start">
                  <span className="rating-extreme-label text-start" style={{ whiteSpace: "pre-line", lineHeight: "1.3" }}>
                    Very{"\n"}Dissatisfied
                  </span>
                  <div className="rating-emoji-circle sad-circle">
                    <img src={sadEmoji} alt="Very Dissatisfied" />
                  </div>
                </div>

                <div className="d-flex flex-column align-items-end">
                  <span className="rating-extreme-label text-end" style={{ whiteSpace: "pre-line", lineHeight: "1.3" }}>
                    Very{"\n"}Satisfied
                  </span>
                  <div className="rating-emoji-circle happy-circle">
                    <img src={happyEmoji} alt="Very Satisfied" />
                  </div>
                </div>
              </div>

              {/* Slider scale container */}
              <div className="rating-slider-container">
                <div className="rating-slider-track"></div>
                <div className="rating-slider-steps-row">
                  {ratingsMap.map(rating => {
                    const isSelected = answers[currentQuestion.question_id]?.ratingId === rating.id;
                    const optionLabel = currentQuestion.options?.[rating.id - 1]?.option_text || rating.label;
                    return (
                      <div
                        key={rating.id}
                        className={`rating-slider-step-node step-${rating.id} ${isSelected ? "selected" : ""}`}
                        onClick={() => handleSelectRating(currentQuestion.question_id, rating.id, optionLabel)}
                        title={optionLabel}
                      >
                        <div className="step-circle-outer">
                          <div className="step-circle-inner"></div>
                        </div>
                        <span className="step-number">{rating.id}</span>
                        {currentQuestion.options?.[rating.id - 1] && <span className="rating-option-label">{optionLabel}</span>}
                        {isSelected && (
                          <div className="selected-rating-popover">
                            <div className="rating-score-badge">{rating.id}/5</div>
                            <div className="rating-label-text">{optionLabel}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {currentQuestion.question_type === "open_end" && (
            <div className="mb-4">
              <label className="cs-label ft-13 fw-semibold mb-2">Your Response</label>
              <textarea
                className="form-control"
                rows="5"
                placeholder="Type your answer here..."
                maxLength={500}
                value={answers[currentQuestion.question_id] || ""}
                onChange={(e) => handleUpdateAnswer(currentQuestion.question_id, e.target.value)}
              />
              <div className="text-end text-muted ft-11 mt-1">
                {(answers[currentQuestion.question_id] || "").length}/500 Characters
              </div>
            </div>
          )}
        </div>

        {/* Footer controls */}
        <div className="survey-actions-footer border-top pt-4 mt-4 d-flex justify-content-between align-items-center">
          <div className="auto-save-label text-success ft-12 d-flex align-items-center gap-2">
            <i className="bi bi-shield-check ft-16" style={{ color: "#10B981" }}></i> Answers are saved when you continue
          </div>
          <div className="d-flex gap-3">
            <button
              className="btn btn-prev px-4 py-2 rounded-3 fw-bold ft-13"
              onClick={handlePrev}
              style={{ visibility: currentQuestionIndex > 0 ? "visible" : "hidden" }}
            >
              &larr; Previous
            </button>
            <button
              className="btn btn-danger btn-next-submit px-4 py-2 rounded-3 fw-bold ft-13 bgred hover-bg-dark border-0"
              onClick={handleNext}
              disabled={submitting}
            >
              {submitting ? "Saving..." : hasChanged ? "Submit" : "Next"} &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TakeSurvey;
